/**
 * Step 4 — 관리자 계정. FE-W-07.
 *
 * - email: local part 입력 + suffix readonly (@<domain>)
 *   → 사용자가 다른 도메인 입력 자체 차단 (defense in depth — backend EMAIL_DOMAIN_MISMATCH 도 검증)
 * - password: 최소 8자 + 강도 색상 indicator (간이)
 * - displayName: optional
 * - submit → finalize → useAuthStore.setSession + navigate /workchat
 *
 * 에러 분기:
 *   - RACE_FINALIZED → /onboarding/race-rejected
 *   - SESSION_CONSUMED → /onboarding/expired
 *   - FINGERPRINT_MISMATCH → /onboarding/expired
 *   - 그 외 → inline alert
 */

import { useState, useSyncExternalStore, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { OnboardingError } from '../../../api/onboarding';
import {
  tOnb, getOnboardingLang, subscribeOnboardingLang,
  type OnboardingI18nKey,
} from '../../../lib/onboarding-i18n';

function passwordStrength(pw: string): { score: 0 | 1 | 2 | 3 | 4; labelKey: OnboardingI18nKey } {
  let s = 0;
  if (pw.length >= 10) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const safe = Math.min(s, 4) as 0 | 1 | 2 | 3 | 4;
  const labelKeys = [
    'onboarding.step4.pw_strength.0',
    'onboarding.step4.pw_strength.1',
    'onboarding.step4.pw_strength.2',
    'onboarding.step4.pw_strength.3',
    'onboarding.step4.pw_strength.4',
  ] as const;
  return { score: safe, labelKey: labelKeys[safe] };
}

export function Step4AdminAccount() {
  useSyncExternalStore(subscribeOnboardingLang, getOnboardingLang, getOnboardingLang);
  const navigate = useNavigate();
  const domain = useOnboardingStore((s) => s.domain);
  const finalizeAction = useOnboardingStore((s) => s.finalizeAction);
  const loading = useOnboardingStore((s) => s.loading);
  const reset = useOnboardingStore((s) => s.reset);

  const [localPart, setLocalPart] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<{ code: string; message: string } | null>(null);

  const trimmedLocal = localPart.trim().toLowerCase();
  const trimmedName = displayName.trim();
  const fullEmail = trimmedLocal && domain ? `${trimmedLocal}@${domain}` : '';
  const localValid = /^[a-z0-9._-]{1,64}$/.test(trimmedLocal);
  const pwStrength = passwordStrength(password);
  const passwordValid = password.length >= 10 && pwStrength.score >= 2;
  const canSubmit = !!domain && localValid && passwordValid && !loading;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setLocalError(null);
    try {
      // Cycle 72 — 승인 워크플로우: finalize 는 토큰/세션을 발급하지 않는다(승인 전 로그인 불가).
      // 즉시 /workchat 진입(setSession) 대신 SYSTEM_ADMIN 승인 대기 안내 페이지로 분기.
      await finalizeAction({
        email: fullEmail,
        password,
        displayName: trimmedName || undefined,
      });
      reset();
      navigate('/onboarding/pending-approval', { replace: true });
    } catch (err) {
      if (err instanceof OnboardingError) {
        if (err.payload.code === 'RACE_FINALIZED') {
          navigate('/onboarding/race-rejected', { replace: true });
          return;
        }
        if (
          err.payload.code === 'SESSION_CONSUMED' ||
          err.payload.code === 'FINGERPRINT_MISMATCH' ||
          err.payload.code === 'SESSION_EXPIRED'
        ) {
          navigate('/onboarding/expired', { replace: true });
          return;
        }
        setLocalError(err.payload);
      }
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} noValidate>
      <h2 className="onboarding-step__heading">{tOnb('onboarding.step4.heading')}</h2>
      <p className="onboarding-step__subheading">
        {tOnb('onboarding.step4.subheading_before_strong')} <strong>{tOnb('onboarding.step4.subheading_role')}</strong> {tOnb('onboarding.step4.subheading_after_strong')}
      </p>

      {localError && (
        <div className="onboarding-step__alert" role="alert">
          {localError.message}
        </div>
      )}

      <div className="onboarding-step__field">
        <label htmlFor="local" className="onboarding-step__label">
          {tOnb('onboarding.step4.label_email')}
        </label>
        <div className="onboarding-step__input-with-suffix">
          <input
            id="local"
            type="text"
            value={localPart}
            onChange={(e) => setLocalPart(e.target.value)}
            placeholder={tOnb('onboarding.step4.placeholder_local')}
            autoComplete="username"
            inputMode="email"
            required
            aria-describedby="email-suffix"
          />
          <span id="email-suffix" className="onboarding-step__suffix" aria-label={`@${domain}`}>
            @{domain ?? ''}
          </span>
        </div>
        {!localValid && trimmedLocal && (
          <p className="onboarding-step__error">
            {tOnb('onboarding.step4.err_local')}
          </p>
        )}
      </div>

      <div className="onboarding-step__field">
        <label htmlFor="password" className="onboarding-step__label">
          {tOnb('onboarding.step4.label_pw')}
        </label>
        <div className="onboarding-step__input-with-suffix">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            maxLength={128}
            required
            aria-describedby="password-strength"
          />
          <button
            type="button"
            className="onboarding-step__suffix"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? tOnb('onboarding.step4.aria_pw_hide') : tOnb('onboarding.step4.aria_pw_show')}
            style={{ cursor: 'pointer', border: 'none', background: 'var(--color-bg-elevated, #f3f4f6)' }}
          >
            {showPassword ? tOnb('onboarding.step4.btn_hide') : tOnb('onboarding.step4.btn_show')}
          </button>
        </div>
        <p
          id="password-strength"
          className="onboarding-step__hint"
          aria-live="polite"
          style={{
            color:
              pwStrength.score >= 3
                ? 'var(--color-success, #15803d)'
                : pwStrength.score >= 2
                ? 'var(--color-text-secondary, #6b7280)'
                : 'var(--color-danger, #b91c1c)',
          }}
        >
          {tOnb('onboarding.step4.pw_strength_prefix')}: {tOnb(pwStrength.labelKey)} {tOnb('onboarding.step4.pw_hint_after_strength')}
        </p>
      </div>

      <div className="onboarding-step__field">
        <label htmlFor="displayName" className="onboarding-step__label">
          {tOnb('onboarding.step4.label_name')}
        </label>
        <input
          id="displayName"
          type="text"
          className="onboarding-step__input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={20}
          placeholder={tOnb('onboarding.step4.placeholder_name')}
          autoComplete="name"
        />
      </div>

      <div className="onboarding-step__actions">
        <button
          type="button"
          className="onboarding-step__button onboarding-step__button--secondary"
          onClick={() => navigate('/onboarding/verify')}
        >
          {tOnb('onboarding.btn.prev')}
        </button>
        <button
          type="submit"
          className="onboarding-step__button onboarding-step__button--primary"
          disabled={!canSubmit}
          aria-busy={loading}
        >
          {loading ? tOnb('onboarding.btn.submitting') : tOnb('onboarding.step4.btn_submit')}
        </button>
      </div>
    </form>
  );
}
